/**
 * Format-agnostisches Zeilen-Parsing fuer den Dividenden-Massenimport (backlog.md ->
 * "Excel-Import/-Vorlage", unabhaengig vom PDF/OCR-Weg) - arbeitet bewusst auf bereits in
 * {headers, rows} zerlegten Strings statt auf rohem Dateitext, damit sowohl die CSV- als
 * auch die .xlsx-Vorlage (dividend-xlsx.ts) dieselbe Spalten-Erkennung/Validierung ohne
 * Duplikat nutzen koennen. Anders als der Broker-CSV-Import fuers Depot
 * (lib/portfolio/import/broker-formats.ts) gibt es hier kein fremdes Broker-Format zu
 * erkennen - die Datei ist Eriks eigene, von Erikonomie vorgegebene Vorlage
 * (siehe buildDividendXlsxTemplate in dividend-xlsx.ts), daher reicht robustes,
 * gross-/kleinschreibungs- und ae/ä-tolerantes Spalten-Matching statt einer eigenen
 * Mapping-UI. Nutzt dieselben generischen Zahlen-/Datums-Parser wie der Depot-Import
 * (lib/portfolio/import/csv-parsing.ts), kein Duplikat.
 */
import { parseFlexibleDate, parseFlexibleNumber } from "@/lib/portfolio/import/csv-parsing";

export interface ParsedDividendRow {
  /** Original-Zeilennummer in der Datei (1-basiert, Kopfzeile ausgenommen) - fuer
   * Fehlermeldungen in der Vorschau ("Zeile 4: ..."). */
  rowNumber: number;
  symbol?: string;
  amount?: number;
  currency: string;
  paymentDate?: string;
  /** Default false (Netto) - gleicher Default wie AddDividendDialog (Erik: eigene
   * Dividende ist meist schon netto ausgezahlt). */
  isGross: boolean;
  /** Erste gefundene Unstimmigkeit dieser Zeile (fehlendes Symbol/ungueltiger Betrag/
   * ungueltiges Datum) - nicht kumulativ, reicht fuer eine verstaendliche Vorschau-
   * Fehlermeldung je Zeile. */
  error?: string;
}

const COLUMN_ALIASES: Record<string, string[]> = {
  // "wkn"/"name"/"firma" ergaenzt (Feedback-Runde 19 Punkt 11, 2026-07-27): die Werte in
  // dieser Spalte akzeptierten schon vorher Firmenname/WKN, nicht nur echte Ticker (siehe
  // handleFile() in import-dividends-dialog.tsx, loest ueber dieselbe /api/search-Route
  // wie die globale Suche auf) - nur die Spalten-KOPF-Erkennung kannte bisher keinen
  // WKN/Name-Alias fuer eigene, nicht von Erikonomies Vorlage stammende Dateien.
  symbol: ["symbol", "ticker", "aktie", "wertpapier", "wkn", "name", "firma", "unternehmen"],
  amount: ["betrag", "amount", "summe"],
  currency: ["waehrung", "währung", "currency"],
  paymentDate: ["zahlungsdatum", "datum", "date", "paymentdate"],
  type: ["typ", "brutto/netto", "bruttonetto", "type"],
};

function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue");
}

function findColumnIndex(headers: string[], field: keyof typeof COLUMN_ALIASES): number {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((h) => COLUMN_ALIASES[field].some((alias) => h === normalizeHeader(alias)));
}

export interface DividendCsvColumnIndices {
  symbol: number;
  amount: number;
  currency: number;
  paymentDate: number;
  type: number;
}

/** null, wenn Symbol/Betrag/Datum (die drei Pflichtfelder) nicht auffindbar sind - dann
 * kann die Datei nicht die erwartete Vorlage sein. Waehrung/Typ sind optional (Default
 * EUR bzw. Netto), wie auch beim manuellen Einzeleintrag ueber AddDividendDialog. */
export function detectDividendCsvColumns(headers: string[]): DividendCsvColumnIndices | null {
  const symbol = findColumnIndex(headers, "symbol");
  const amount = findColumnIndex(headers, "amount");
  const paymentDate = findColumnIndex(headers, "paymentDate");
  if (symbol === -1 || amount === -1 || paymentDate === -1) return null;
  return {
    symbol,
    amount,
    currency: findColumnIndex(headers, "currency"),
    paymentDate,
    type: findColumnIndex(headers, "type"),
  };
}

function parseIsGross(raw: string | undefined): boolean {
  if (!raw) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === "brutto" || normalized === "gross";
}

export function parseDividendCsvRows(headers: string[], rows: string[][]): ParsedDividendRow[] | null {
  const columns = detectDividendCsvColumns(headers);
  if (!columns) return null;

  return rows.map((row, i) => {
    const symbol = row[columns.symbol]?.trim() || undefined;
    const amount = row[columns.amount] !== undefined ? parseFlexibleNumber(row[columns.amount]) : undefined;
    const paymentDate = row[columns.paymentDate] !== undefined ? parseFlexibleDate(row[columns.paymentDate]) : undefined;
    const currency = (columns.currency !== -1 ? row[columns.currency]?.trim() : "") || "EUR";
    const isGross = parseIsGross(columns.type !== -1 ? row[columns.type] : undefined);

    let error: string | undefined;
    if (!symbol) error = "Kein Symbol/Ticker/Firmenname/WKN angegeben.";
    else if (amount === undefined || amount <= 0) error = "Betrag fehlt oder ist ungültig.";
    else if (!paymentDate) error = "Datum fehlt oder ist ungültig (erwartet TT.MM.JJJJ oder JJJJ-MM-TT).";
    else if (paymentDate > new Date().toISOString().slice(0, 10)) error = "Datum liegt in der Zukunft.";

    return { rowNumber: i + 1, symbol, amount, currency, paymentDate, isGross, error };
  });
}
